import { useMemo, useState } from 'react';
import { ICountryExchangeDetail } from 'types/countries';
import { useCatalogExchange } from 'hooks/useCatalogExchange';
import { NudosButton } from 'components/NudosComponents';
import UploadForm from 'views/bulkUpload/components/UploadForm/UploadForm';
import { IProductType } from '../../DownloadUploadCatalog.types';
import { UploadFormOption, UploadFormOptionDropdown } from 'views/bulkUpload/components/UploadForm/UploadForm.types';
import { renderDropdownOptionsDownload } from '../../utils/renderOptions';
import {
  downloadProductsToQuoteTemplate,
  downloadTemplate,
  downloadTemplateWithoutData
} from 'services/bulkUpload/uploadCatalog.service';
import { downloadTemplateInfo } from '../../content/downloadUploadCatalogInfo';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import './DownloadCatalogStep0.scss';

interface DownloadCatalogStep0Props {
  selectedCountry: ICountryExchangeDetail | undefined;
  handleSelectedCountry: (country: ICountryExchangeDetail) => void;
  nextStep: () => void;
  replaceCatalog: boolean;
  handleReplaceCatalog: (value: boolean) => void;
  disableReplaceCatalog: boolean;
}

const DownloadCatalogStep0 = ({
  selectedCountry,
  handleSelectedCountry,
  nextStep,
  replaceCatalog
}: DownloadCatalogStep0Props) => {
  const { countries } = useCatalogExchange();
  const { isOnlyDevsAutomates } = useOnlyDevelopers();
  const [selectedProductType, setSelectedProductType] = useState<IProductType | undefined>();
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

  const dropdownOptionsDownload = useMemo<UploadFormOptionDropdown[]>(
    () =>
      renderDropdownOptionsDownload(
        countries,
        selectedCountry,
        handleSelectedCountry,
        selectedProductType,
        setSelectedProductType,
        isOnlyDevsAutomates
      ),
    [countries, handleSelectedCountry, selectedCountry, selectedProductType]
  );

  const DownloadFormOptions = useMemo<UploadFormOption[]>(
    () => [...dropdownOptionsDownload],
    [dropdownOptionsDownload]
  );

  const handleOpenDownloadedTemplate = async () => {
    if (!selectedCountry || !selectedProductType) return;

    const productTypeId = selectedProductType?.id;
    const countryId = selectedCountry?.id;

    let templateUrl: string;

    switch (productTypeId) {
      case 3:
        templateUrl = await downloadProductsToQuoteTemplate(countryId);
        break;
      case 4:
        templateUrl = await downloadTemplateWithoutData();
        break;
      case 1:
      case 2:
      default: {
        const onlyActiveProducts = productTypeId === 2 ? true : null;
        const includeQuoteProducts = productTypeId === 1 ? isOnlyDevsAutomates : null;
        const template = await downloadTemplate(countryId, productTypeId, onlyActiveProducts, includeQuoteProducts);
        templateUrl = template?.url;
        break;
      }
    }

    window.open(templateUrl);
  };

  const handleButtonDownloadTemplate = async () => {
    setIsDownloadingTemplate(true);
    try {
      await handleOpenDownloadedTemplate();
      nextStep();
    } catch (error) {
      displayErrorNotification({ customJSXMessage: <>Error al descargar la plantilla</> });
    }
    setIsDownloadingTemplate(false);
  };

  return (
    <UploadForm
      options={DownloadFormOptions}
      backLink="/bulk-upload/type-of-charge"
      confirmationModal={replaceCatalog}
      fileInputIsDisabled={!selectedCountry}
      uploadInfo={downloadTemplateInfo}
      showSeparatorInOptions={false}
      hideFileInput
      hideAlert
      customClassName="downloadUploadCatalogFormStep0"
      customSubmitSection={
        <NudosButton
          buttonText="Descargar plantilla y continuar"
          handleClick={handleButtonDownloadTemplate}
          isButtonDisabled={!selectedCountry || !selectedProductType}
          customWidth="263px"
          customHeight="44px"
          isButtonLoading={isDownloadingTemplate}
        />
      }
    />
  );
};

export default DownloadCatalogStep0;
